import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Loader2, Camera, MailCheck, ArrowLeft } from "lucide-react";
import GoogleSignInButton from "@/components/GoogleSignInButton";

function VerifyEmailStep({ email, name, onSkip, onVerified }) {
  const { verifyEmail } = useAuth();
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(60);
  React.useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const verify = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await verifyEmail(email, otp);
      toast.success("Email verified — welcome to Eventom");
      onVerified?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Invalid code"); }
    finally { setBusy(false); }
  };
  const resend = async () => {
    try {
      await api.post("/auth/send-verification", { email });
      setCooldown(60);
      toast.success("New code sent (check your email or logs)");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  return (
    <div className="card-dark p-8" data-testid="verify-email-card">
      <div className="chip bg-lime text-ink mb-4">Step 2 · Verify</div>
      <h2 className="font-display uppercase text-3xl md:text-4xl leading-[0.95]">Check your email, {name?.split(" ")[0] || "friend"}.</h2>
      <p className="mt-3 text-sm text-text/70 font-mono">We sent a 6-digit code to <span className="text-lime">{email}</span>. It expires in 5 minutes.</p>
      <form onSubmit={verify} className="mt-6 space-y-3">
        <input required data-testid="verify-otp" inputMode="numeric" pattern="\d{6}" maxLength={6} placeholder="6-digit code" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} className="input-dark text-center text-2xl tracking-[0.5em] font-display" />
        <button data-testid="verify-submit" disabled={busy || otp.length !== 6} className="btn-lime w-full justify-center">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MailCheck className="w-4 h-4" />} {busy ? "Verifying..." : "Verify email"}
        </button>
      </form>
      <div className="mt-4 flex items-center justify-between text-xs font-mono">
        <button onClick={resend} disabled={cooldown > 0} data-testid="verify-resend" className="text-lime disabled:text-muted underline">
          {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
        </button>
        <button onClick={onSkip} data-testid="verify-skip" className="text-muted underline">Skip for now →</button>
      </div>
      <p className="mt-4 text-[10px] font-mono text-muted leading-relaxed">Come, complete your profile and enjoy 😉 — you can verify anytime from your account.</p>
    </div>
  );
}

export default function Signup() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", profile_picture: "" });
  const [preview, setPreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("form"); // form | verify

  // P1.1: Resume OTP verification screen on page refresh if there's a pending verify.
  React.useEffect(() => {
    const pendingEmail = localStorage.getItem("pending_verify_email");
    if (pendingEmail && step === "form") {
      setForm((f) => ({ ...f, email: pendingEmail, name: localStorage.getItem("pending_verify_name") || "" }));
      setStep("verify");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 2 * 1024 * 1024) return toast.error("Image must be under 2MB");
    const reader = new FileReader();
    reader.onload = () => { setForm((p) => ({ ...p, profile_picture: reader.result })); setPreview(reader.result); };
    reader.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const user = await register(form); // backend forces role='user'
      toast.success(`Account created — welcome, ${user.name}!`);
      setStep("verify");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Signup failed"); }
    finally { setLoading(false); }
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto px-4 py-16">
        {step === "form" ? (
          <div className="card-dark p-8">
            <div className="chip bg-pink text-white mb-4">Sign Up · Attendee</div>
            <h1 className="font-display uppercase text-3xl md:text-4xl lg:text-5xl leading-[0.95]">Join the movement.</h1>
            <p className="mt-2 text-sm text-text/70">Basic details now — the rest can wait.</p>
            <form onSubmit={submit} className="mt-6 space-y-3">
              <div className="flex items-center gap-3">
                <label className="cursor-pointer" data-testid="signup-avatar-label">
                  <div className="icon-circle bg-white/5 text-white overflow-hidden !w-16 !h-16 border-2 border-lime/40">
                    {preview ? <img src={preview} alt="" className="w-full h-full object-cover" /> : <Camera className="w-5 h-5" />}
                  </div>
                  <input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="signup-avatar-input" />
                </label>
                <div className="text-xs text-muted font-mono">Profile picture (optional, max 2MB)</div>
              </div>
              <input required data-testid="signup-name" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-dark" />
              <input required data-testid="signup-email" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-dark" />
              <input required data-testid="signup-phone" type="tel" placeholder="Mobile number (e.g. +91 9XXXXXXXXX)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input-dark" />
              <input required data-testid="signup-password" type="password" placeholder="Password (min 6 chars)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="input-dark" />
              <button data-testid="signup-submit" disabled={loading} className="btn-pink w-full justify-center">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} {loading ? "Creating..." : "Create Account"}
              </button>
            </form>
            <div className="my-5 flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-muted">
              <div className="flex-1 h-px bg-white/10" /> or <div className="flex-1 h-px bg-white/10" />
            </div>
            <GoogleSignInButton label="Sign up with Google" testId="signup-google" />
            <div className="mt-6 text-sm font-mono text-muted space-y-1">
              <div>Already have an account? <Link to="/login" data-testid="signup-goto-login" className="text-lime font-bold underline">Log in</Link></div>
              <div>Running events? <Link to="/organizer/signup" data-testid="signup-goto-organiser" className="text-pink font-bold underline">Sign up as Organiser →</Link></div>
            </div>
          </div>
        ) : (
          <>
            <button onClick={() => setStep("form")} className="mb-4 flex items-center gap-2 text-xs font-mono uppercase text-muted"><ArrowLeft className="w-3.5 h-3.5" /> Back</button>
            <VerifyEmailStep email={form.email} name={form.name} onSkip={() => nav("/")} onVerified={() => nav("/")} />
          </>
        )}
      </div>
    </Layout>
  );
}
